import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button } from '@mui/material';
import { useBatchEdit } from './context';
import { AddRemove } from './fields/editorial/AddRemove';
import { DragModal, DragModalHeader } from 'components/DragModal/DragModal';

type BatchEditModalHeaderProps = {
  headerTxt: string;
};

/**
 * BatchEditModalHeader
 */
const BatchEditModalHeader = memo(({ headerTxt }: BatchEditModalHeaderProps) => {
  return <DragModalHeader text={headerTxt} />;
});
BatchEditModalHeader.displayName = 'BatchEditModalHeader';

type BatchEditModalBodyProps = {
  onHandleSubmit: React.FormEventHandler<HTMLFormElement>;
};

/**
 * BatchEditModalBody
 */
const BatchEditModalBody = memo(({ onHandleSubmit }: BatchEditModalBodyProps) => {
  return (
    <form
      id="batch-edit-form"
      data-testid="batch-edit-form"
      onSubmit={onHandleSubmit}
    >
      <AddRemove />
    </form>
  );
});
BatchEditModalBody.displayName = 'BatchEditModalBody';

type BatchEditModalFooterProps = {
  isModifyEnabled: boolean;
  handleCancel: () => void;
};

/**
 * BatchEditModalFooter
 */
const BatchEditModalFooter = memo(
  ({ isModifyEnabled, handleCancel }: BatchEditModalFooterProps) => {
    const { t } = useTranslation();
    const modifyTxt = t('blox.actions.modify');

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1,
          width: '100%',
        }}
      >
        <Button onClick={handleCancel}>{t('blox.actions.cancel')}</Button>
        <Button
          type="submit"
          form="batch-edit-form"
          variant="contained"
          disabled={!isModifyEnabled}
        >
          {modifyTxt}
        </Button>
      </Box>
    );
  },
);
BatchEditModalFooter.displayName = 'BatchEditModalFooter';

/**
 * BatchEditForm
 */
export const BatchEditForm = memo(() => {
  const { t } = useTranslation();
  const { dialogOpen, toggleDialog, handleSubmit, addRemoveValues, modifySearchResults } =
    useBatchEdit();

  const handleCancel = useCallback(() => {
    toggleDialog();
  }, [toggleDialog]);

  // Check if any item in any groups
  // arrays has an action in misc
  const isModifyEnabled = useMemo(() => {
    if (!addRemoveValues) return false;
    return Object.values(addRemoveValues).some(
      (group) => Array.isArray(group) && group.some((item) => item?.misc?.action),
    );
  }, [addRemoveValues]);

  const batchHeaderTxt = t('blox.batch_edit.label');
  const searchOrSelected = modifySearchResults
    ? t('blox.general.search_results')
    : t('blox.general.selected_assets');
  const modalHeaderTxt = `${batchHeaderTxt}: ${searchOrSelected}`;

  return (
    <DragModal
      open={dialogOpen}
      onClose={handleCancel}
      header={<BatchEditModalHeader headerTxt={modalHeaderTxt} />}
      body={<BatchEditModalBody onHandleSubmit={handleSubmit} />}
      footer={
        <BatchEditModalFooter
          isModifyEnabled={isModifyEnabled}
          handleCancel={handleCancel}
        />
      }
      preventBackdropClickClose={true}
      initialWidth={550}
      isResizable={true}
      isDraggable={true}
      isFullscreenable={true}
    />
  );
});

BatchEditForm.displayName = 'BatchEditForm';
